import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useCouponBroadcast } from '@/hooks/coupon-broadcast';
import { useClaimCouponMutation } from '@/store/api';

import { playCouponChime } from './coupon-chime';
import {
	claimErrorMessage,
	dismissCoupon,
	EMPTY_STORE,
	expiresWithinADay,
	expiryLabel,
	formatCouponValue,
	type ICouponBroadcastMessage,
	type ICouponStore,
	type IHeldCoupon,
	isTargeted,
	markClaimed,
	receiveCoupon,
	removeHeld,
	undoDismiss,
	visibleCoupons,
} from './held-coupons';

export type ClaimState = { status: 'claiming' } | { status: 'failed'; message: string };

const UNDO_MS = 8000;
const CLOCK_MS = 60_000;

interface Identity {
	/** Wallet-service user GUID — what broadcast `userIds` and the claim body use. */
	userId: string | undefined;
	walletId: string | undefined;
}

export interface HeldCoupons {
	coupons: IHeldCoupon[];
	/** Unclaimed coupons — what the badge counts. */
	waiting: number;
	/** Some unclaimed coupon expires within a day. */
	urgent: boolean;
	now: number;
	claims: Record<string, ClaimState>;
	claim: (coupon: IHeldCoupon) => Promise<void>;
	dismiss: (coupon: IHeldCoupon) => void;
	undo: IHeldCoupon | null;
	restore: () => void;
	/** Polite live-region text for the latest arrival. */
	announcement: string;
	/** The most recent new arrival; `seq` changes on every one, even a repeat of the same coupon. */
	arrival: { coupon: IHeldCoupon; seq: number } | null;
}

/**
 * Broadcast coupons for this user, held in memory for the session (deliberately not persisted).
 * Owns the hub subscription, so it must live in something mounted on every page — the header
 * wallet widget — for arrivals to be caught while its popover is closed.
 */
export const useHeldCoupons = ({ userId, walletId }: Identity): HeldCoupons => {
	const [store, setStore] = useState<ICouponStore>(EMPTY_STORE);
	const storeRef = useRef(store);
	const [now, setNow] = useState(() => Date.now());
	const [claims, setClaims] = useState<Record<string, ClaimState>>({});
	const [undo, setUndo] = useState<IHeldCoupon | null>(null);
	const [announcement, setAnnouncement] = useState('');
	const [arrival, setArrival] = useState<HeldCoupons['arrival']>(null);
	const [claimCoupon] = useClaimCouponMutation();

	// Arrivals that land before the user id resolves wait here, then go through the same
	// targeting check. Showing a coupon without knowing who this is would repeat the old bug
	// (coupons appearing for people they weren't sent to).
	const userIdRef = useRef(userId);
	userIdRef.current = userId;
	const queueRef = useRef<ICouponBroadcastMessage[]>([]);

	const commit = useCallback((next: ICouponStore) => {
		storeRef.current = next;
		setStore(next);
	}, []);

	const accept = useCallback(
		(message: ICouponBroadcastMessage, me: string) => {
			if (!isTargeted(message, me)) return;
			const at = Date.now();
			const { store: next, added } = receiveCoupon(storeRef.current, message, at);
			commit(next);
			setNow(at);
			if (!added) return;
			const coupon = next.held.find((h) => h.couponId === message.couponId);
			if (coupon) setArrival((prev) => ({ coupon, seq: (prev?.seq ?? 0) + 1 }));
			playCouponChime(at);
			setAnnouncement(`New coupon: ${formatCouponValue(message)} on your next top-up. ${expiryLabel(message.expiresAt, at)}.`);
		},
		[commit]
	);

	const onArrival = useCallback(
		(message: ICouponBroadcastMessage) => {
			const me = userIdRef.current;
			if (me) accept(message, me);
			else queueRef.current.push(message);
		},
		[accept]
	);

	useCouponBroadcast(onArrival);

	useEffect(() => {
		if (!userId || !queueRef.current.length) return;
		const queued = queueRef.current;
		queueRef.current = [];
		queued.forEach((message) => accept(message, userId));
	}, [userId, accept]);

	const coupons = useMemo(() => visibleCoupons(store, now), [store, now]);
	const unclaimed = coupons.filter((c) => !c.claimedAt);

	// "Expires today" and drop-offs depend on the clock; tick while anything is held.
	useEffect(() => {
		if (!coupons.length) return;
		const timer = window.setInterval(() => setNow(Date.now()), CLOCK_MS);
		return () => window.clearInterval(timer);
	}, [coupons.length]);

	useEffect(() => {
		if (!undo) return;
		const timer = window.setTimeout(() => setUndo(null), UNDO_MS);
		return () => window.clearTimeout(timer);
	}, [undo]);

	const claim = useCallback(
		async (coupon: IHeldCoupon) => {
			if (!userId || !walletId) {
				setClaims((prev) => ({
					...prev,
					[coupon.couponId]: { status: 'failed', message: "Your wallet isn't ready yet. Try again in a moment." },
				}));
				return;
			}
			setClaims((prev) => ({ ...prev, [coupon.couponId]: { status: 'claiming' } }));
			try {
				await claimCoupon({ couponId: coupon.couponId, walletId, userId }).unwrap();
				commit(markClaimed(storeRef.current, coupon.couponId, Date.now()));
				setClaims((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => id !== coupon.couponId)));
			} catch (error) {
				setClaims((prev) => ({ ...prev, [coupon.couponId]: { status: 'failed', message: claimErrorMessage(error) } }));
			}
		},
		[claimCoupon, commit, userId, walletId]
	);

	const dismiss = useCallback(
		(coupon: IHeldCoupon) => {
			if (coupon.claimedAt) {
				commit(removeHeld(storeRef.current, coupon.couponId));
				return;
			}
			commit(dismissCoupon(storeRef.current, coupon.couponId, Date.now()));
			setUndo(coupon);
		},
		[commit]
	);

	const restore = useCallback(() => {
		if (!undo) return;
		commit(undoDismiss(storeRef.current, undo));
		setUndo(null);
	}, [commit, undo]);

	return {
		coupons,
		waiting: unclaimed.length,
		urgent: unclaimed.some((c) => expiresWithinADay(c.expiresAt, now)),
		now,
		claims,
		claim,
		dismiss,
		undo,
		restore,
		announcement,
		arrival,
	};
};
